import React, {useEffect} from 'react';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {zodResolver} from '@hookform/resolvers/zod';
import {useUpdateProject, type UpdateProjectBody} from 'api/generated';
import {useParams} from 'react-router-dom';
import {Button, InputText, Transitioner} from 'components';

import useProjectStore from '../../stores/useProjectStore';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Link is required'),
  recordAnalytics: z.boolean().optional(),
});

interface ISidebarLeftSettingsProps {}

const SidebarLeftSettings = ({}: ISidebarLeftSettingsProps) => {
  const params = useParams();
  const uid = params.uid;

  const isMine = useProjectStore(s => s.isMine);
  const handle = useProjectStore(s => s.creatorHandle) ?? '';
  const updateProject = useProjectStore(s => s.updateProject);

  const update = useUpdateProject();
  const {
    register,
    handleSubmit,
    reset: resetFormState,
    formState: {errors, isDirty},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const name = useProjectStore(s => s.name);
  const slug = useProjectStore(s => s.slug);
  const recordAnalytics = useProjectStore(s => s.recordAnalytics);

  useEffect(() => {
    resetFormState({name, slug, recordAnalytics});
  }, [name, slug, recordAnalytics, resetFormState]);

  const onSubmit = async (data: UpdateProjectBody) => {
    if (uid) {
      const {data: res} = await update.mutateAsync({uid, data});
      if (res.data) {
        toast.success('Project updated');
        updateProject(res.data);
        resetFormState(res.data);
      } else if (res.errors) {
        toast.error(res.errors[0].detail);
      }
    }
  };

  return (
    <Transitioner fromY={-20} delay={0.1}>
      <h4 className="text-slate-800 dark:text-white font-semibold pb-4">Settings</h4>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
        <InputText readOnly={!isMine} label="Name" {...register('name')} fieldSize="small" error={errors.name?.message} />
        <InputText readOnly={!isMine} label="Link" {...register('slug')} fieldSize="small" prefix={`/ar/${handle}/`} error={errors.slug?.message} />
        <div className="mt-5 flex justify-end">
          <div>
            <Button type="submit" text="Save" fullWidth size="extra-small" variant="secondary" disabled={!isDirty} loading={update.isPending} />
          </div>
        </div>
      </form>
    </Transitioner>
  );
};

export default React.memo(SidebarLeftSettings);
